import { Line, ReelWinPosition } from '@cgs/data-access';
import { Logger } from '@cgs/shared';
import { Container, IntervalAction, SequenceAction } from '@cgs/wge';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { SpinConfig } from '../../../../reels_engine/game_config/game_config';
import { ReelsSoundModel } from '../../../../reels_engine/reels_sound_model';
import {
  T_IconsSoundModelComponent,
  T_IFadeReelsProvider,
  T_ILinesModelProvider,
  T_IWinLineStrategyProvider,
  T_LinesSceneObjectComponent,
} from '../../../../type_definitions';
import { IconsSoundModelComponent } from '../../icons_sound_model_component';
import { ILinesModelProvider } from '../../interfaces/i_lines_model_provider';
import { LinesSceneObjectComponent } from '../../lines_scene_object_component';
import { AllWinLinesAction } from '../complex_win_line_actions/all_win_lines_action';
import { MorphingWinLineAction } from '../complex_win_line_actions/morphing_lines_action';
import { NWaysLinesAction } from '../complex_win_line_actions/nways_lines_action';
import { ShortNWaysLinesAction } from '../complex_win_line_actions/short_nways_lines_action';
import { ShortWinLinesAction } from '../complex_win_line_actions/short_win_lines_action';
import { SpecialNWaysAction } from '../complex_win_line_actions/special_nways_action';
import { SpecialWinLinesAction } from '../complex_win_line_actions/special_win_lines_action';
import { IFadeReelsProvider } from '../i_fade_reels_provider';
import { IWinLineStrategyProvider } from '../win_line_strategy_providers/i_win_line_strategy_provider';

export class WinLineActionComponent {
  public winLineAction: IntervalAction;
  public shortWinLineAction: IntervalAction;
  public specialLineAction: IntervalAction;

  protected readonly container: Container;
  private readonly _repeatWinLineSound: boolean;

  constructor(container: Container, repeatWinLineSound: boolean = true) {
    this.container = container;
    this._repeatWinLineSound = repeatWinLineSound;
    Logger.Debug('load ' + this.constructor.name);
  }

  protected get repeatWinLineSound(): boolean {
    return this._repeatWinLineSound;
  }

  public update(
    reelsSoundModel: ReelsSoundModel,
    winLines: Line[],
    viewReels: number[][],
    spinConfig: SpinConfig,
    winPositions: ReelWinPosition[]
  ): void {
    const lineModel = this.container.resolve<ILinesModelProvider>(T_ILinesModelProvider).lineModel;
    const lineNode = this.container.resolve<LinesSceneObjectComponent>(
      T_LinesSceneObjectComponent
    ).linesSceneObject;

    const winLineAction = new AllWinLinesAction(this.container, winLines);
    const shortWinLineAction = new ShortWinLinesAction(
      this.container,
      winLines,
      winPositions,
      lineNode,
      lineModel,
      spinConfig,
      reelsSoundModel
    );
    const specialLineAction = new SpecialWinLinesAction(this.container, winLines, winPositions);

    this.winLineAction = new SequenceAction([shortWinLineAction, winLineAction]);
    this.shortWinLineAction = shortWinLineAction;
    this.specialLineAction = specialLineAction;

    this.enableFade(winLines, winPositions);
  }

  protected enableFade(winLines: Line[], winPositions: ReelWinPosition[]): void {
    const stateMachine = this.container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    if (
      (winLines && winLines.length > 0) ||
      (winPositions &&
        winPositions.length > 0 &&
        (stateMachine.curResponse.isScatter ||
          stateMachine.curResponse.isBonus ||
          stateMachine.curResponse.freeSpinsInfo ||
          winPositions.some((x) => x.type === 'PayingSpecSymbol')))
    ) {
      this.container.resolve<IFadeReelsProvider>(T_IFadeReelsProvider).enableFade(true);
    }
  }
}

export class MorphWinLineActionComponent extends WinLineActionComponent {
  protected _strategyProvider: IWinLineStrategyProvider;

  constructor(container: Container, repeatWinLineSound: boolean = true) {
    super(container, repeatWinLineSound);
    this._strategyProvider = container.resolve<IWinLineStrategyProvider>(
      T_IWinLineStrategyProvider
    );
  }

  protected get strategyProvider(): IWinLineStrategyProvider {
    return this._strategyProvider;
  }

  update(
    reelsSoundModel: ReelsSoundModel,
    winLines: Line[],
    viewReels: number[][],
    spinConfig: SpinConfig,
    winPositions: ReelWinPosition[]
  ): void {
    super.update(reelsSoundModel, winLines, viewReels, spinConfig, winPositions);

    const lineModel = this.container.resolve<ILinesModelProvider>(T_ILinesModelProvider).lineModel;
    const lineNode = this.container.resolve<LinesSceneObjectComponent>(
      T_LinesSceneObjectComponent
    ).linesSceneObject;
    const iconSoundModel = this.container.resolve<IconsSoundModelComponent>(
      T_IconsSoundModelComponent
    ).iconsSoundModel;

    const winLineAction = new MorphingWinLineAction(
      this.container,
      lineNode,
      lineModel,
      spinConfig,
      winLines,
      winPositions,
      iconSoundModel,
      reelsSoundModel,
      this.repeatWinLineSound
    );
    const shortWinLineAction = new ShortWinLinesAction(
      this.container,
      winLines,
      winPositions,
      lineNode,
      lineModel,
      spinConfig,
      reelsSoundModel
    );
    const specialLineAction = new SpecialNWaysAction(
      this.container,
      winLines,
      spinConfig,
      winPositions
    );

    if (this.strategyProvider) {
      const winLineActipPart = this.strategyProvider
        .getWinLineStrategy()
        .applyStrategy(winLineAction);
      this.shortWinLineAction = this.strategyProvider
        .getShortWinLineStrategy()
        .applyStrategy(shortWinLineAction);
      this.specialLineAction = this.strategyProvider
        .getSpecialWinLineStrategy()
        .applyStrategy(specialLineAction);

      this.winLineAction = new SequenceAction([shortWinLineAction, winLineActipPart]);
    } else {
      this.winLineAction = new SequenceAction([shortWinLineAction, winLineAction]);
      this.shortWinLineAction = shortWinLineAction;
      this.specialLineAction = specialLineAction;
    }
  }
}

export class NWaysLineActionProvider extends WinLineActionComponent {
  constructor(container: Container, repeatWinLineSound: boolean = true) {
    super(container, repeatWinLineSound);
  }

  public update(
    reelsSoundModel: ReelsSoundModel,
    winLines: Line[],
    viewReels: number[][],
    spinConfig: SpinConfig,
    winPositions: ReelWinPosition[]
  ): void {
    super.update(reelsSoundModel, winLines, viewReels, spinConfig, winPositions);

    const shortWinLineAction = new SequenceAction([
      new ShortNWaysLinesAction(this.container, winLines, winPositions, spinConfig),
    ]);
    const winLineAction = new NWaysLinesAction(
      this.container,
      winLines,
      winPositions,
      spinConfig,
      this.repeatWinLineSound
    );
    const specialLineAction = new SpecialNWaysAction(
      this.container,
      winLines,
      spinConfig,
      winPositions
    );

    this.winLineAction = new SequenceAction([shortWinLineAction, winLineAction]);

    this.shortWinLineAction = shortWinLineAction;

    this.specialLineAction = specialLineAction;
  }
}
